import { useState } from 'react';
import { CheckCircle2, X, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

export interface ResponseEditorProps {
  value: string;
  originalResponse: string;
  onChange: (value: string) => void;
  onSave: () => void;
  onCancel: () => void;
  maxLength?: number;
  className?: string;
}

export function ResponseEditor({
  value,
  originalResponse,
  onChange,
  onSave,
  onCancel,
  maxLength = 5000,
  className,
}: ResponseEditorProps) {
  const [showSaveAsCanonical, setShowSaveAsCanonical] = useState(false);
  const charCount = value.length;
  const hasChanges = value !== originalResponse;

  const handleReset = () => {
    onChange(originalResponse);
  };

  return (
    <div className={cn('space-y-3', className)}>
      <div className="relative">
        <Textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="min-h-[150px] resize-y"
          placeholder="Enter your modified response..."
        />
        <div className="absolute bottom-2 right-2 text-xs text-muted-foreground">
          {charCount.toLocaleString()} / {maxLength.toLocaleString()}
        </div>
      </div>

      {hasChanges && (
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="save-canonical"
            checked={showSaveAsCanonical}
            onChange={(e) => setShowSaveAsCanonical(e.target.checked)}
            className="rounded border-gray-300"
          />
          <label htmlFor="save-canonical" className="text-sm text-muted-foreground">
            Save as canonical response for future use
          </label>
        </div>
      )}

      <div className="flex items-center justify-between pt-2 border-t">
        <div className="flex items-center gap-2">
          <Button variant="success" onClick={onSave} disabled={!hasChanges}>
            <CheckCircle2 className="h-4 w-4 mr-1.5" />
            Save & Approve
          </Button>
          <Button variant="outline" onClick={onCancel}>
            <X className="h-4 w-4 mr-1.5" />
            Cancel
          </Button>
        </div>
        {hasChanges && (
          <Button variant="ghost" size="sm" onClick={handleReset}>
            <RotateCcw className="h-4 w-4 mr-1.5" />
            Reset
          </Button>
        )}
      </div>
    </div>
  );
}
